import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

dayjs.extend(relativeTime);

const FILTERS = [
  { value: "all", label: "All activity" },
  { value: "resource", label: "Resources" },
  { value: "user", label: "Users" },
  { value: "organisation", label: "Organisations" },
  { value: "action", label: "Actions" },
  { value: "plan", label: "Plans" },
  { value: "priority", label: "Priorities" },
  { value: "kpi", label: "KPIs" },
  { value: "coaching_session", label: "Coaching" },
  { value: "issue", label: "Issues" },
  { value: "decision", label: "Decisions" },
];

const VERB_STYLE = {
  created: { icon: "Plus", cls: "bg-[#3D8B65]/12 text-[#3D8B65]" },
  updated: { icon: "Pencil", cls: "bg-[#3E6DB5]/12 text-[#3E6DB5]" },
  deleted: { icon: "Trash2", cls: "bg-[#C95555]/12 text-[#C95555]" },
  assigned: { icon: "Share2", cls: "bg-[#FFB000]/15 text-[#B87F1E]" },
  unassigned: { icon: "Unlink", cls: "bg-[#1E2D44]/10 text-[#1E2D44]/60" },
  invited: { icon: "UserPlus", cls: "bg-[#3D8B65]/12 text-[#3D8B65]" },
  completed: { icon: "CheckCircle2", cls: "bg-[#3D8B65]/12 text-[#3D8B65]" },
};

const ENTITY_LABEL = {
  resource: "resource", user: "user", organisation: "organisation", action: "action",
  plan: "plan", priority: "priority", kpi: "KPI", coaching_session: "coaching session",
  issue: "issue", decision: "decision", checkin: "check-in", assessment: "assessment",
};

export default function AuditLog() {
  const [logs, setLogs] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLogs(null);
    api.get(`/activity?entity_type=${filter}&limit=200`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, [filter]);

  return (
    <div data-testid="audit-page">
      <PageHeader
        eyebrow="Governance"
        title="Activity & audit log"
        subtitle="A record of who changed what across the platform — including resource and user edits and deletions."
        icon="ScrollText"
        actions={
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-[180px] bg-white" data-testid="audit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        }
      />

      {!logs ? (
        <PageLoading />
      ) : logs.length === 0 ? (
        <EmptyState icon="ScrollText" title="No activity yet" description="Changes made across the platform will appear here." />
      ) : (
        <div className="amden-card overflow-hidden p-0" data-testid="audit-list">
          {logs.map((l, i) => {
            const vs = VERB_STYLE[l.verb] || { icon: "Circle", cls: "bg-[#1E2D44]/10 text-[#1E2D44]/60" };
            const VIcon = Icons[vs.icon] || Icons.Circle;
            return (
              <div key={l.id || i} className="flex items-start gap-3 border-b border-[#EEF1F6] px-4 py-3.5 last:border-0 hover:bg-[#F9FAFC]" data-testid="audit-row">
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${vs.cls}`}><VIcon size={15} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#0B1729]">
                    <span className="font-semibold">{l.actor_name || "Someone"}</span>{" "}
                    <span className="text-[#1E2D44]/70">{l.verb} {ENTITY_LABEL[l.entity_type] || l.entity_type}</span>
                  </p>
                  <p className="mt-0.5 truncate text-xs text-[#1E2D44]/60">{l.summary}</p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[11px] font-medium text-[#1E2D44]/55">{l.company_name || "Amden Library"}</div>
                  <div className="text-[11px] text-[#1E2D44]/40" title={dayjs(l.created_at).format("DD MMM YYYY, HH:mm")}>{dayjs(l.created_at).fromNow()}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
